import { parse } from "query-string";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import {
    PopularFeedItemToken,
    blogsPopularFeedFetchTrigger,
} from "@/mk2/apps/blogs/containers/BlogsPopularFeed/BlogsPopularFeed.actions";
import {
    getBlogsPopularFeedSearchState,
    getBlogsPopularFeedState,
} from "@/mk2/apps/blogs/containers/BlogsPopularFeed/BlogsPopularFeed.selectors";
import {
    blogsDeletePostTrigger,
    blogsIgnoreUserTrigger,
    blogsLoadUserGroupMembershipsTrigger,
    blogsSetDisplayingAtFeedTrigger,
    blogsSetPostVisibilityTrigger,
} from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.actions";
import blogsSharedReducer from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.reducers";
import blogsSharedSaga from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas";
import { getBlogspSharedState } from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.selectors";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import { BLOG_POST_MENU_MORE, BLOG_POST_VISIBILITY_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import { blogFeedHeaderTabs } from "@/mk2/apps/blogs/utils";
import { emptyComplaintsCounts } from "@/mk2/apps/groups/containers/GroupReportedFeed/GroupReportedFeed.constants";
import {
    getCommentComplaintsCounts,
    getPostsCommentsCount,
    getPostsFromFeedItemsCached,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { Count } from "@/mk2/components/NotificationsBadge";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { getComplaintsShownForBlogPosts } from "@/mk2/containers/Complaints/Complaints.selectors";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { FeedItem, FeedItemOrGroup } from "@/mk2/containers/PageHeader/PageHeader.reducers";
import { memoizeOne } from "@/mk2/helpers/cache";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getPageHeaderState,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    getRequestUserAgent,
} from "@/mk2/reducers";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import {
    BlogFeedItemEntity,
    BlogFeedItemSchema,
    BlogPostEntity,
    HeaderMenuItem,
    MenuSelection,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { BLOGS_WALL, TAB_BLOGS_POPULAR } from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import BlogsPopularFeed from "./BlogsPopularFeed";
import reducer, {
    BlogsPopularFeedPageState,
    BlogsPopularFeedState,
} from "./BlogsPopularFeed.reducers";
import saga from "./BlogsPopularFeed.sagas";

export enum ScrollToPosition {
    FirstFeedItem = "first",
    LastFeedItem = "last",
}

interface OwnProps {}

interface StateProps {
    commentsCounts: { [postId: number]: number };
    complaintsCounts: { [feedItemId: number]: number };
    complaintsOpened: boolean[];
    errorMessage: string;
    feedsTabsLastVisitCounts: {
        [tabName: string]: Count;
    };
    isIOS: boolean;
    loadingState: LoadingState;
    isMobile: boolean;
    requestUserPermissions: string[];
    feedItems: BlogFeedItemEntity[];
    hasMore: boolean;
    // query: string;
    requestUser: UserEntity;
    userAgent: UserAgentParsed;
    membershipsInGroups: number[];
}

interface DispatchProps {
    onDelete(postId: number);
    onIgnoreUser(username: string);
    onLoad(after?: PopularFeedItemToken);
    onLoadUserGroupMemberships();
    onPostToggleMenuMore(postId: number);
    onPostToggleVisibilityMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetVisibility(postId: number, visibility: PostVisibility);
}

type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

interface Query {
    query: string;
}

const emptyDict = {};

export default class BlogsPopularFeedPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private parseQuery = memoizeOne((qs: string): Query => {
        const qp = parse(qs) || {};
        return {
            query: qp.query !== undefined ? (qp.query as string) : null,
        };
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const parsedQuery: Query = this.parseQuery(ownProps.location.search);
            const query = parsedQuery.query;

            const pageHeaderState = getPageHeaderState(state);
            const feedsFeedItem: FeedItemOrGroup[] = pageHeaderState.feedMenu.filter(
                (fi) => fi.name === "feeds",
            );
            const feedsTabsLastVisitCounts =
                feedsFeedItem.length > 0
                    ? (feedsFeedItem[0] as FeedItem).tabsLastVisitCounts
                    : emptyDict;

            const blogsPopularFeedState: BlogsPopularFeedState = getBlogsPopularFeedState(state);

            const requestUserPermissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);
            const userAgent = getRequestUserAgent(state);

            const isAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;

            const blogsPopularFeedPageState: BlogsPopularFeedPageState =
                getBlogsPopularFeedSearchState(state, query);

            const feedItemIds = blogsPopularFeedPageState.feedItemIds;

            const feedItems: BlogFeedItemEntity[] = getDenormalizedEntities<BlogFeedItemEntity>(
                state,
                BlogFeedItemSchema,
                feedItemIds,
            );

            const commentsLoadingState = blogsPopularFeedState.commentsLoadingState;

            const feedItemsPosts: BlogPostEntity[] = getPostsFromFeedItemsCached(feedItems);
            const commentsCounts = getPostsCommentsCount(state, feedItemsPosts);

            const complaintsCounts = isAdmin
                ? getCommentComplaintsCounts(state, feedItemIds, commentsLoadingState)
                : emptyComplaintsCounts;

            const blogsSharedState = getBlogspSharedState(state);

            return {
                commentsCounts,
                complaintsCounts,
                complaintsOpened: getComplaintsShownForBlogPosts(state, feedItemsPosts),
                errorMessage: blogsPopularFeedPageState.errorMessage,
                feedsTabsLastVisitCounts,
                hasMore: blogsPopularFeedPageState.hasMore,
                isIOS: getRequestDeviceIsIOS(state),
                loadingState: blogsPopularFeedPageState.loadingState,
                isMobile: getRequestDeviceMobile(state),
                requestUserPermissions,
                feedItems,
                // query,
                requestUser,
                userAgent,
                membershipsInGroups: blogsSharedState.userGroups.memberships,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onDelete: blogsDeletePostTrigger,
            onIgnoreUser: blogsIgnoreUserTrigger,
            onLoad: blogsPopularFeedFetchTrigger,
            onLoadUserGroupMemberships: blogsLoadUserGroupMembershipsTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onPostToggleVisibilityMenuMore: (postId: number) =>
                menuToggle(BLOG_POST_VISIBILITY_MENU_MORE(postId)),
            onReport: complaintOpenTrigger,
            onSetDisplayingAtFeed: blogsSetDisplayingAtFeedTrigger,
            onSetVisibility: blogsSetPostVisibilityTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        return `${TAB_BLOGS_POPULAR} - ${BLOGS_WALL}`;
    }

    public render(props: Props): false | JSX.Element {
        return <BlogsPopularFeed {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return false;
    }

    public getHeaderTabs(props: Props): JSX.Element[] | null {
        const { feedsTabsLastVisitCounts, requestUser } = props;
        return blogFeedHeaderTabs("popular", requestUser, feedsTabsLastVisitCounts);
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "photoblog",
            activeItem: HeaderMenuItem.BlogFeeds,
        };
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { isMobile } = props;
        if (!isMobile) {
            return {
                zone: "Blogs Feed - Halfpage Sticky (Desktop)",
            };
        }
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: "Blogs - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Blogs - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { feedItems, requestUser, isMobile } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!feedItems || feedItems.length < 10) {
            return null;
        }

        return {
            zone: "Blogs Feed - Anchor Ad Leaderboard (Mobile)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.blogs.blogsPopularFeed", reducer);
        store.injectSaga("containers.blogs.blogsPopularFeed", saga);

        store.injectReducer("containers.blogs.blogsShared", blogsSharedReducer);
        store.injectSaga("containers.blogs.blogsShared", blogsSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
